/**
 * Adapted from PlayCanvas Engine's gsplat-octree-instance.js state ownership at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import * as THREE from 'three'
import { NUM_BUCKETS } from '@/plugins/gsplat/lod/constants'
import { GSPLAT_ALLOC_IDS } from '@/plugins/gsplat/unified/GSplatAllocId'
import type { GSplatPlacement } from '@/plugins/gsplat/unified/GSplatPlacement'
import type { Disposable } from '@/shared/types/core'
import type { GSplatLodRefinementMode } from '@/shared/types/gsplat'
import type { GSplatOctree, GSplatOctreeFileResource, GSplatOctreeNodeLod } from './GSplatOctree'

export interface GSplatStreamingInvalidationSink {
  invalidateWorld(reason: 'lod-target-changed' | 'lod-resource-ready' | 'lod-dispose'): void
  requestFrame(reason: 'lod-resource-ready'): void
}

export interface GSplatDeviceLossSource {
  onDeviceLost(listener: () => void): Disposable
}

export interface GSplatOctreeInstanceOptions {
  readonly deviceLossSource?: GSplatDeviceLossSource
}

/** PlayCanvas immediate node-AABB debug submission 的最小适配接口。 */
export interface GSplatOctreeDebugScene {
  readonly gsplat: { readonly debug: string }
  readonly immediate: {
    drawWireAlignedBox(
      min: THREE.Vector3,
      max: THREE.Vector3,
      color: Readonly<{ r: number; g: number; b: number; a: number }>,
      depthTest: boolean,
      layer: unknown
    ): void
  }
  readonly defaultDrawLayer: unknown
}

export interface GSplatPendingDecrement {
  readonly oldFileIndex: number
  readonly newFileIndex: number
}

export interface GSplatOctreeLodParams {
  readonly lodBehindPenalty: number
  readonly lodUnderfillLimit: number
  readonly lodRefinementMode?: GSplatLodRefinementMode
}

export interface GSplatOctreeReleaseReceipt {
  readonly fileIndices: readonly number[]
  readonly placementCount: number
  commit(): void
  rollback(): void
}

export interface GSplatOctreeTransitionStage {
  readonly changed: boolean
  readonly activePlacements: readonly GSplatPlacement[]
  commit(): void
  rollback(): void
  finalize(): void
}

export interface GSplatOctreeLodEvaluationStage {
  readonly totalSplats: number
  commit(): void
  rollback(): void
  finalize(): void
}

interface RetiredPlacement {
  readonly fileIndex: number
  readonly placement: GSplatPlacement
}

interface PlacementSnapshot {
  readonly resource: GSplatPlacement['resource']
  readonly intervals: ReadonlyMap<number, { readonly start: number; readonly end: number }>
}

interface InstanceSnapshot {
  readonly targetNodeFiles: ReadonlyMap<number, number>
  readonly targetNodeLods: ReadonlyMap<number, number>
  readonly activeNodeFiles: ReadonlyMap<number, number>
  readonly residentFileIndices: ReadonlySet<number>
  readonly pending: ReadonlySet<number>
  readonly prefetchPending: ReadonlySet<number>
  readonly pendingDecrements: ReadonlyMap<number, GSplatPendingDecrement>
  readonly pendingVisibleAdds: ReadonlyMap<number, number>
  readonly removedCandidates: ReadonlySet<number>
  readonly activePlacements: ReadonlySet<GSplatPlacement>
  readonly environmentPlacement: GSplatPlacement | null
  readonly filePlacements: readonly (GSplatPlacement | null)[]
  readonly retiredPlacements: readonly RetiredPlacement[]
  readonly placementStates: ReadonlyMap<GSplatPlacement, PlacementSnapshot>
  readonly ownedFiles: ReadonlySet<number>
  readonly loadInterestFiles: ReadonlySet<number>
  readonly prefetchInterestFiles: ReadonlySet<number>
  readonly currentLods: readonly number[]
  readonly optimalLods: readonly number[]
  readonly worldDistances: readonly number[]
  readonly budgetBuckets: readonly number[]
  readonly rangeMin: number
  readonly rangeMax: number
  readonly revision: number
  readonly needsLodUpdate: boolean
  readonly dirtyModifiedPlacements: boolean
  readonly dirtyPlacementSetChanged: boolean
  readonly environmentVisible: boolean
}

const REF_TAN_HALF_FOV = Math.tan(THREE.MathUtils.degToRad(22.5))
const LOD_DEBUG_COLORS = Object.freeze([
  Object.freeze({ r: 1, g: 0, b: 0, a: 1 }),
  Object.freeze({ r: 0, g: 1, b: 0, a: 1 }),
  Object.freeze({ r: 0, g: 0, b: 1, a: 1 }),
  Object.freeze({ r: 1, g: 1, b: 0, a: 1 }),
  Object.freeze({ r: 1, g: 0, b: 1, a: 1 }),
])

export class GSplatOctreeNodeInfo {
  currentLod = -1
  optimalLod = -1
  worldDistance = 0
  colorAccumulatedTranslation = 0
  budgetBucket = 0
  readonly allocId = GSPLAT_ALLOC_IDS.get()
  inst: GSplatOctreeInstance<GSplatOctreeFileResource> | null = null
  lods: readonly GSplatOctreeNodeLod[] | null = null

  resetLod(): void {
    this.currentLod = -1
    this.optimalLod = -1
  }
}

/** 每个 View/Layer Manager 独有的 target/active/pending Octree selection owner。 */
export class GSplatOctreeInstance<TResource extends GSplatOctreeFileResource>
  implements Disposable
{
  readonly nodeInfos: GSplatOctreeNodeInfo[]
  readonly filePlacements: Array<GSplatPlacement | null>
  readonly activePlacements = new Set<GSplatPlacement>()
  readonly pending = new Set<number>()
  readonly prefetchPending = new Set<number>()
  readonly pendingDecrements = new Map<number, GSplatPendingDecrement>()
  readonly pendingVisibleAdds = new Map<number, number>()
  readonly removedCandidates = new Set<number>()
  readonly targetNodeFiles = new Map<number, number>()
  readonly targetNodeLods = new Map<number, number>()
  readonly activeNodeFiles = new Map<number, number>()
  readonly residentFileIndices = new Set<number>()
  readonly previousPosition = new THREE.Vector3()
  environmentPlacement: GSplatPlacement | null = null
  rangeMin = 0
  rangeMax = 0
  splatBudget = 0
  needsLodUpdate = true
  dirtyModifiedPlacements = false
  dirtyPlacementSetChanged = false

  private readonly ownedFiles = new Set<number>()
  private readonly loadInterestFiles = new Set<number>()
  private readonly prefetchInterestFiles = new Set<number>()
  private readonly retiredPlacements: RetiredPlacement[] = []
  private readonly inverseWorld = new THREE.Matrix4()
  private readonly localCameraPosition = new THREE.Vector3()
  private readonly localCameraForward = new THREE.Vector3()
  private readonly worldCameraPosition = new THREE.Vector3()
  private readonly worldCameraForward = new THREE.Vector3()
  private readonly uniformScale = new THREE.Vector3()
  private readonly debugAabb = new THREE.Box3()
  private deviceLostLease: Disposable | null = null
  private ownsEnvironmentRef = false
  private lodMinDistanceThresholds: Float32Array | null = null
  private revisionValue = 0
  private lifecycleGeneration = 1
  private destroyed = false

  constructor(
    readonly octree: GSplatOctree<TResource>,
    readonly placement: GSplatPlacement,
    private readonly invalidation: GSplatStreamingInvalidationSink,
    options: GSplatOctreeInstanceOptions = {}
  ) {
    this.nodeInfos = octree.nodes.map(() => {
      const info = new GSplatOctreeNodeInfo()
      info.inst = this as unknown as GSplatOctreeInstance<GSplatOctreeFileResource>
      return info
    })
    this.filePlacements = new Array(octree.files.length).fill(null)
    this.rangeMax = Math.max(0, octree.lodLevels - 1)
    if (octree.environmentUrl) {
      octree.incEnvironmentRefCount()
      this.ownsEnvironmentRef = true
      try {
        octree.ensureEnvironmentResource()
      } catch (error) {
        octree.decEnvironmentRefCount()
        this.ownsEnvironmentRef = false
        throw error
      }
    }
    try {
      this.deviceLostLease =
        options.deviceLossSource?.onDeviceLost(() => this.onDeviceLost()) ?? null
    } catch (error) {
      if (this.ownsEnvironmentRef) {
        octree.decEnvironmentRefCount()
        this.ownsEnvironmentRef = false
      }
      throw error
    }
  }

  get revision(): number {
    return this.revisionValue
  }

  get pendingLoadCount(): number {
    const pendingFiles = new Set([...this.pending, ...this.prefetchPending])
    return pendingFiles.size + (this.octree.environmentUrl && !this.environmentPlacement ? 1 : 0)
  }

  get targetFileIndices(): ReadonlySet<number> {
    return new Set(this.targetNodeFiles.values())
  }

  get activeFileIndices(): ReadonlySet<number> {
    return new Set(this.activeNodeFiles.values())
  }

  get environmentConfigured(): boolean {
    return this.octree.environmentUrl !== null
  }

  get environmentVisible(): boolean {
    return this.placement.environmentVisible
  }

  setEnvironmentVisible(visible: boolean): boolean {
    if (!this.environmentConfigured) return false
    if (this.placement.environmentVisible === visible) return true
    this.placement.environmentVisible = visible
    this.syncEnvironmentVisibility()
    return true
  }

  syncEnvironmentVisibility(): boolean {
    if (!this.environmentPlacement?.resource) return false
    const shouldBeActive = this.placement.environmentVisible
    const isActive = this.activePlacements.has(this.environmentPlacement)
    if (shouldBeActive === isActive) return false
    if (shouldBeActive) this.activePlacements.add(this.environmentPlacement)
    else this.activePlacements.delete(this.environmentPlacement)
    this.dirtyModifiedPlacements = true
    this.dirtyPlacementSetChanged = true
    this.revisionValue++
    return true
  }

  /**
   * 发布新的 node→LOD target。已激活旧 LOD 会保持到新文件 ready；不可见 target 立即移除。
   */
  setTargetLods(targets: ReadonlyMap<number, number>): void {
    const stage = this.stageTargetLods(targets)
    try {
      stage.commit()
    } catch (error) {
      stage.rollback()
      throw error
    }
    stage.finalize()
  }

  /** LOD + WorldState 编排入口：stage 可见 prospective placements，失败可完整恢复本实例 baseline。 */
  stageTargetLods(targets: ReadonlyMap<number, number>): GSplatOctreeTransitionStage {
    this.assertActive()
    const snapshot = this.captureSnapshot()
    const generation = this.lifecycleGeneration
    try {
      this.applyTargetLods(targets)
    } catch (error) {
      if (generation === this.lifecycleGeneration) this.restoreSnapshot(snapshot)
      throw error
    }
    const worldChanged =
      !mapsEqual(snapshot.targetNodeFiles, this.targetNodeFiles) ||
      !mapsEqual(snapshot.targetNodeLods, this.targetNodeLods) ||
      snapshot.currentLods.some((lod, index) => lod !== this.nodeInfos[index].currentLod)
    let committed = false
    let settled = false
    return {
      changed: worldChanged,
      activePlacements: Object.freeze([...this.activePlacements]),
      commit: () => {
        if (settled || committed) return
        this.assertStageGeneration(generation)
        committed = true
      },
      rollback: () => {
        if (settled) return
        if (generation !== this.lifecycleGeneration) {
          settled = true
          return
        }
        try {
          this.restoreSnapshot(snapshot)
        } finally {
          settled = true
        }
      },
      finalize: () => {
        if (settled) return
        if (generation !== this.lifecycleGeneration) {
          settled = true
          return
        }
        if (!committed) {
          this.restoreSnapshot(snapshot)
          settled = true
          return
        }
        settled = true
        if (worldChanged) this.invalidation.invalidateWorld('lod-target-changed')
      },
    }
  }

  private applyTargetLods(targets: ReadonlyMap<number, number>): void {
    this.assertActive()
    const plannedTargets = this.planTargets(targets)
    if (
      mapsEqual(this.targetNodeFiles, plannedTargets.files) &&
      mapsEqual(this.targetNodeLods, plannedTargets.lods)
    ) {
      return
    }
    const nextTargetFiles = new Set(plannedTargets.files.values())
    const newlyOwned: number[] = []
    const newlyInterested: number[] = []
    const stagedPlacements = new Map<number, GSplatPlacement>()
    const readyResources = new Map<number, TResource>()

    try {
      for (const fileIndex of nextTargetFiles) {
        if (!this.ownedFiles.has(fileIndex)) {
          this.octree.incRefCount(fileIndex)
          this.ownedFiles.add(fileIndex)
          newlyOwned.push(fileIndex)
        }
        const resource = this.octree.ensureFileResource(fileIndex)
        if (resource) readyResources.set(fileIndex, resource)
        else if (!this.loadInterestFiles.has(fileIndex)) {
          this.octree.beginLoadInterest(fileIndex)
          this.loadInterestFiles.add(fileIndex)
          newlyInterested.push(fileIndex)
        }
        if (
          !this.filePlacements[fileIndex] &&
          typeof this.placement.createChildPlacement === 'function'
        ) {
          stagedPlacements.set(
            fileIndex,
            this.placement.createChildPlacement(null, this.octree.files[fileIndex]?.lodLevel ?? 0)
          )
        }
      }
    } catch (error) {
      for (const staged of stagedPlacements.values()) staged.destroy()
      for (const fileIndex of newlyInterested) {
        this.loadInterestFiles.delete(fileIndex)
        this.octree.endLoadInterest(fileIndex)
      }
      for (const fileIndex of newlyOwned) {
        this.ownedFiles.delete(fileIndex)
        this.octree.decRefCount(fileIndex, 0)
      }
      throw error
    }

    for (const [fileIndex, staged] of stagedPlacements) {
      this.filePlacements[fileIndex] = staged
      this.removedCandidates.delete(fileIndex)
    }

    const previousTargets = new Map(this.targetNodeFiles)
    this.targetNodeFiles.clear()
    this.targetNodeLods.clear()
    for (const [nodeIndex, fileIndex] of plannedTargets.files) {
      this.targetNodeFiles.set(nodeIndex, fileIndex)
      this.targetNodeLods.set(nodeIndex, plannedTargets.lods.get(nodeIndex)!)
    }

    let activeChanged = false
    for (const [nodeIndex, oldActiveFile] of [...this.activeNodeFiles]) {
      if (plannedTargets.files.has(nodeIndex)) continue
      this.activeNodeFiles.delete(nodeIndex)
      this.nodeInfos[nodeIndex].currentLod = -1
      this.removedCandidates.add(oldActiveFile)
      activeChanged = true
    }

    this.pendingVisibleAdds.clear()
    this.pendingDecrements.clear()
    for (const [nodeIndex, fileIndex] of plannedTargets.files) {
      const resource = readyResources.get(fileIndex)
      if (resource) {
        this.residentFileIndices.add(fileIndex)
        this.finishLoadInterest(fileIndex)
        activeChanged =
          this.promoteNode(nodeIndex, fileIndex, plannedTargets.lods.get(nodeIndex)!) ||
          activeChanged
        continue
      }
      const oldFileIndex = this.activeNodeFiles.get(nodeIndex)
      if (oldFileIndex === undefined) this.pendingVisibleAdds.set(nodeIndex, fileIndex)
      else if (oldFileIndex !== fileIndex) {
        this.pendingDecrements.set(nodeIndex, { oldFileIndex, newFileIndex: fileIndex })
      }
    }

    for (const fileIndex of this.loadInterestFiles) {
      if (!nextTargetFiles.has(fileIndex)) this.finishLoadInterest(fileIndex)
    }
    const requiredFiles = this.collectRequiredFiles()
    for (const fileIndex of this.ownedFiles) {
      if (!requiredFiles.has(fileIndex)) this.removedCandidates.add(fileIndex)
    }
    this.refreshPending()
    this.syncFilePlacements()
    this.revisionValue++
    this.needsLodUpdate = true
    if (activeChanged || !mapsEqual(previousTargets, plannedTargets.files)) {
      this.dirtyModifiedPlacements = true
    }
  }

  selectDesiredLodIndex(
    nodeIndex: number,
    optimalLodIndex: number,
    maxLod: number,
    lodUnderfillLimit: number
  ): number {
    const node = this.octree.nodes[nodeIndex]
    if (!node) throw new Error(`非法 GSplat Octree nodeIndex: ${nodeIndex}`)
    if (lodUnderfillLimit > 0) {
      const allowedMaxCoarseLod = Math.min(maxLod, optimalLodIndex + lodUnderfillLimit)
      for (let lodIndex = optimalLodIndex; lodIndex <= allowedMaxCoarseLod; lodIndex++) {
        const fileIndex = node.lods[lodIndex]?.fileIndex ?? -1
        if (fileIndex !== -1 && this.octree.getFileResource(fileIndex)) return lodIndex
      }
      for (let lodIndex = allowedMaxCoarseLod; lodIndex >= optimalLodIndex; lodIndex--) {
        if ((node.lods[lodIndex]?.fileIndex ?? -1) !== -1) return lodIndex
      }
    }
    return optimalLodIndex
  }

  prefetchNextLod(
    nodeIndex: number,
    desiredLodIndex: number,
    optimalLodIndex: number,
    refinementMode: GSplatLodRefinementMode = 'step'
  ): void {
    if (desiredLodIndex === -1 || optimalLodIndex === -1) return
    const node = this.octree.nodes[nodeIndex]
    if (!node) throw new Error(`非法 GSplat Octree nodeIndex: ${nodeIndex}`)
    if (desiredLodIndex === optimalLodIndex) {
      this.prefetchLod(node.lods[optimalLodIndex]?.fileIndex ?? -1)
      return
    }
    const direct = refinementMode === 'direct'
    const startLod = direct ? optimalLodIndex : Math.max(optimalLodIndex, desiredLodIndex - 1)
    const endLod = direct ? desiredLodIndex : optimalLodIndex
    const step = direct ? 1 : -1
    for (
      let lodIndex = startLod;
      direct ? lodIndex < endLod : lodIndex >= endLod;
      lodIndex += step
    ) {
      const fileIndex = node.lods[lodIndex]?.fileIndex ?? -1
      if (fileIndex === -1) continue
      this.prefetchLod(fileIndex)
      break
    }
  }

  updateLod(camera: THREE.Camera, params: GSplatOctreeLodParams): void {
    const stage = this.stageLodUpdate(camera, params)
    try {
      stage.commit()
    } catch (error) {
      stage.rollback()
      throw error
    }
    stage.finalize()
  }

  stageLodUpdate(camera: THREE.Camera, params: GSplatOctreeLodParams): GSplatOctreeTransitionStage {
    this.assertActive()
    this.validateLodParams(params)
    const snapshot = this.captureSnapshot()
    const generation = this.lifecycleGeneration
    const maxLod = this.octree.lodLevels - 1
    const range = this.resolveRange(maxLod)
    this.rangeMin = range.min
    this.rangeMax = range.max
    const uniformScale = Math.abs(this.placement.node.getWorldScale(this.uniformScale).x)
    try {
      this.evaluateNodeLods(
        camera,
        maxLod,
        this.placement.lodBaseDistance,
        this.placement.lodMultiplier,
        range.min,
        range.max,
        params,
        uniformScale,
        false,
        0
      )
      this.applyDesiredLodChanges(maxLod, params)
    } catch (error) {
      if (generation === this.lifecycleGeneration) this.restoreSnapshot(snapshot)
      throw error
    }
    return this.createLodTransitionStage(snapshot, generation)
  }

  evaluateOptimalLods(
    camera: THREE.Camera,
    params: GSplatOctreeLodParams,
    budgetScale = 1,
    globalMaxDistanceForBuckets = 0
  ): number {
    const stage = this.stageOptimalLods(camera, params, budgetScale, globalMaxDistanceForBuckets)
    stage.commit()
    stage.finalize()
    return stage.totalSplats
  }

  stageOptimalLods(
    camera: THREE.Camera,
    params: GSplatOctreeLodParams,
    budgetScale = 1,
    globalMaxDistanceForBuckets = 0
  ): GSplatOctreeLodEvaluationStage {
    this.assertActive()
    if (!Number.isFinite(budgetScale) || budgetScale <= 0) {
      throw new Error(`非法 GSplat budgetScale: ${budgetScale}`)
    }
    this.validateLodParams(params)
    if (!Number.isFinite(globalMaxDistanceForBuckets) || globalMaxDistanceForBuckets < 0) {
      throw new Error(`非法 GSplat globalMaxDistanceForBuckets: ${globalMaxDistanceForBuckets}`)
    }
    const previousRange = { min: this.rangeMin, max: this.rangeMax }
    const generation = this.lifecycleGeneration
    const previousNodes = this.nodeInfos.map((info) => ({
      optimalLod: info.optimalLod,
      worldDistance: info.worldDistance,
      budgetBucket: info.budgetBucket,
    }))
    const maxLod = this.octree.lodLevels - 1
    const range = this.resolveRange(maxLod)
    this.rangeMin = range.min
    this.rangeMax = range.max
    const uniformScale = Math.abs(this.placement.node.getWorldScale(this.uniformScale).x)
    const effectiveBase = this.placement.lodBaseDistance * budgetScale
    const effectiveMultiplier = Math.max(1.2, this.placement.lodMultiplier * budgetScale ** -0.2)
    let totalSplats: number
    try {
      totalSplats = this.evaluateNodeLods(
        camera,
        maxLod,
        effectiveBase,
        effectiveMultiplier,
        range.min,
        range.max,
        params,
        uniformScale,
        true,
        globalMaxDistanceForBuckets
      )
    } catch (error) {
      this.restoreLodEvaluation(previousRange, previousNodes)
      throw error
    }
    let committed = false
    let settled = false
    return {
      totalSplats,
      commit: () => {
        if (settled || committed) return
        this.assertStageGeneration(generation)
        committed = true
      },
      rollback: () => {
        if (settled) return
        if (generation !== this.lifecycleGeneration) {
          settled = true
          return
        }
        this.restoreLodEvaluation(previousRange, previousNodes)
        settled = true
      },
      finalize: () => {
        if (settled) return
        if (generation !== this.lifecycleGeneration) {
          settled = true
          return
        }
        if (!committed) this.restoreLodEvaluation(previousRange, previousNodes)
        settled = true
      },
    }
  }

  applyLodChanges(maxLod: number, params: GSplatOctreeLodParams): void {
    const stage = this.stageLodChanges(maxLod, params)
    try {
      stage.commit()
    } catch (error) {
      stage.rollback()
      throw error
    }
    stage.finalize()
  }

  stageLodChanges(
    maxLod: number,
    params: GSplatOctreeLodParams,
    optimalLodOverrides?: readonly number[]
  ): GSplatOctreeTransitionStage {
    this.assertActive()
    this.validateLodParams(params)
    if (optimalLodOverrides && optimalLodOverrides.length !== this.nodeInfos.length) {
      throw new Error(
        `GSplat budget LOD override 长度不匹配: actual=${optimalLodOverrides.length}, expected=${this.nodeInfos.length}`
      )
    }
    const snapshot = this.captureSnapshot()
    const generation = this.lifecycleGeneration
    try {
      this.applyDesiredLodChanges(maxLod, params, optimalLodOverrides)
    } catch (error) {
      if (generation === this.lifecycleGeneration) this.restoreSnapshot(snapshot)
      throw error
    }
    return this.createLodTransitionStage(snapshot, generation)
  }

  private applyDesiredLodChanges(
    maxLod: number,
    params: GSplatOctreeLodParams,
    optimalLodOverrides?: readonly number[]
  ): void {
    const targets = new Map<number, number>()
    for (let nodeIndex = 0; nodeIndex < this.octree.nodes.length; nodeIndex++) {
      const optimalLodIndex =
        optimalLodOverrides?.[nodeIndex] ?? this.nodeInfos[nodeIndex].optimalLod
      if (
        !Number.isSafeInteger(optimalLodIndex) ||
        optimalLodIndex < -1 ||
        optimalLodIndex > maxLod
      ) {
        throw new Error(
          `非法 GSplat budget LOD override: node=${nodeIndex}, lod=${optimalLodIndex}`
        )
      }
      const desiredLodIndex = this.selectDesiredLodIndex(
        nodeIndex,
        optimalLodIndex,
        maxLod,
        params.lodUnderfillLimit
      )
      if (desiredLodIndex >= 0) targets.set(nodeIndex, desiredLodIndex)
      this.prefetchNextLod(
        nodeIndex,
        desiredLodIndex,
        optimalLodIndex,
        params.lodRefinementMode ?? 'step'
      )
    }
    this.applyTargetLods(targets)
  }

  testMoved(threshold: number): boolean {
    return (
      this.placement.node
        .getWorldPosition(this.worldCameraPosition)
        .distanceTo(this.previousPosition) > threshold
    )
  }

  updateMoved(): void {
    this.placement.node.getWorldPosition(this.previousPosition)
  }

  /** Poll fire-and-forget loader results；只有 live target 可以晋升 active。 */
  pollCompletions(): boolean {
    const stage = this.stageCompletions()
    try {
      stage.commit()
    } catch (error) {
      stage.rollback()
      throw error
    }
    stage.finalize()
    return stage.changed
  }

  /** file/environment ready 与 WorldState publication 的两阶段入口。 */
  stageCompletions(): GSplatOctreeTransitionStage {
    this.assertActive()
    const snapshot = this.captureSnapshot()
    const generation = this.lifecycleGeneration
    let activeChanged: boolean
    try {
      activeChanged = this.applyCompletions()
    } catch (error) {
      if (generation === this.lifecycleGeneration) this.restoreSnapshot(snapshot)
      throw error
    }
    const prefetchChanged = !setsEqual(snapshot.prefetchPending, this.prefetchPending)
    let committed = false
    let settled = false
    return {
      changed: activeChanged,
      activePlacements: Object.freeze([...this.activePlacements]),
      commit: () => {
        if (settled || committed) return
        this.assertStageGeneration(generation)
        committed = true
      },
      rollback: () => {
        if (settled) return
        if (generation !== this.lifecycleGeneration) {
          settled = true
          return
        }
        try {
          this.restoreSnapshot(snapshot)
        } finally {
          settled = true
        }
      },
      finalize: () => {
        if (settled) return
        if (generation !== this.lifecycleGeneration) {
          settled = true
          return
        }
        if (!committed) {
          this.restoreSnapshot(snapshot)
          settled = true
          return
        }
        settled = true
        if (activeChanged) this.invalidation.invalidateWorld('lod-resource-ready')
        if (activeChanged || prefetchChanged) {
          this.invalidation.requestFrame('lod-resource-ready')
        }
      },
    }
  }

  private applyCompletions(): boolean {
    this.assertActive()
    let activeChanged = false
    for (const [nodeIndex, fileIndex] of this.targetNodeFiles) {
      const resource = this.octree.ensureFileResource(fileIndex)
      if (!resource) continue
      this.residentFileIndices.add(fileIndex)
      this.finishLoadInterest(fileIndex)
      activeChanged =
        this.promoteNode(nodeIndex, fileIndex, this.targetNodeLods.get(nodeIndex)!) || activeChanged
    }
    this.refreshPending()
    const prefetchCompleted = this.pollPrefetchCompletions()
    const environmentChanged = this.pollEnvironmentCompletion()
    activeChanged = environmentChanged || activeChanged
    if (!activeChanged) {
      if (prefetchCompleted) {
        this.needsLodUpdate = true
      }
      return false
    }

    this.syncFilePlacements()
    this.revisionValue++
    this.needsLodUpdate = true
    this.dirtyModifiedPlacements = true
    return true
  }

  /** 对齐上游每帧 update：modified dirty 在返回时消费，另外两个 latch 由各自 consume 保留。 */
  update(): boolean {
    this.assertActive()
    if (this.placement.lodDirty) {
      this.placement.lodDirty = false
      this.needsLodUpdate = true
    }
    this.pollCompletions()
    const dirty = this.dirtyModifiedPlacements
    this.dirtyModifiedPlacements = false
    return dirty
  }

  consumeModifiedPlacements(): boolean {
    const value = this.dirtyModifiedPlacements
    this.dirtyModifiedPlacements = false
    return value
  }

  consumePlacementSetChanged(): boolean {
    const value = this.dirtyPlacementSetChanged
    this.dirtyPlacementSetChanged = false
    return value
  }

  consumeNeedsLodUpdate(): boolean {
    const value = this.needsLodUpdate
    this.needsLodUpdate = false
    return value
  }

  /** 保持冻结上游 node-aabbs 诊断顺序，scratch 为 per-instance 以允许多 View 重入。 */
  debugRender(scene: GSplatOctreeDebugScene): void {
    if (scene.gsplat.debug !== 'node-aabbs') return
    const worldMatrix = this.placement.node.matrixWorld
    for (let nodeIndex = 0; nodeIndex < this.octree.nodes.length; nodeIndex++) {
      const lodIndex = this.nodeInfos[nodeIndex].currentLod
      if (lodIndex < 0) continue
      const color = LOD_DEBUG_COLORS[Math.min(lodIndex, LOD_DEBUG_COLORS.length - 1)]
      this.debugAabb.copy(this.octree.nodes[nodeIndex].bounds).applyMatrix4(worldMatrix)
      scene.immediate.drawWireAlignedBox(
        this.debugAabb.min,
        this.debugAabb.max,
        color,
        true,
        scene.defaultDrawLayer
      )
    }
  }

  /** WorldState 成功移除旧 intervals 后才提交的显式 release receipt。 */
  stagePendingReleases(): GSplatOctreeReleaseReceipt {
    this.assertActive()
    const fileIndices = [...this.removedCandidates]
    const placements = [...this.retiredPlacements]
    const generation = this.lifecycleGeneration
    let settled = false
    return {
      fileIndices: Object.freeze(fileIndices),
      placementCount: placements.length,
      commit: () => {
        if (settled) return
        settled = true
        if (generation !== this.lifecycleGeneration) return
        const required = this.collectRequiredFiles()
        for (const fileIndex of fileIndices) {
          if (
            required.has(fileIndex) ||
            !this.removedCandidates.has(fileIndex) ||
            !this.ownedFiles.delete(fileIndex)
          ) {
            continue
          }
          this.removedCandidates.delete(fileIndex)
          this.octree.decRefCount(fileIndex)
        }
        for (const retired of placements) {
          const index = this.retiredPlacements.indexOf(retired)
          if (index === -1) continue
          this.retiredPlacements.splice(index, 1)
          retired.placement.destroy()
        }
      },
      rollback: () => {
        settled = true
      },
    }
  }

  /**
   * World 已发布不再包含本 Instance 的新状态后，由该状态首次成功 materialize 时消费。
   * file ref 在 receipt commit 前保持不变，避免旧 render-ready WorkBuffer 仍在使用时提前卸载。
   */
  stageRetirement(): GSplatOctreeReleaseReceipt {
    this.assertActive()
    const fileIndices = [...this.ownedFiles]
    const generation = this.lifecycleGeneration
    const placementCount =
      this.activePlacements.size +
      this.retiredPlacements.length +
      (this.environmentPlacement ? 1 : 0)
    let settled = false
    return {
      fileIndices: Object.freeze(fileIndices),
      placementCount,
      commit: () => {
        if (settled) return
        settled = true
        if (generation !== this.lifecycleGeneration) return
        let failure: unknown
        const cleanup = (operation: () => void): void => {
          try {
            operation()
          } catch (error) {
            failure ??= error
          }
        }

        // destroy() 对仍在 ownedFiles 中的项使用 zero-cooldown；先移出本次 receipt
        // 捕获的 refs，再在 terminal cleanup 后按 Octree 的正常 cooldown 规则释放。
        for (const fileIndex of fileIndices) this.ownedFiles.delete(fileIndex)
        cleanup(() => this.destroy())
        for (const fileIndex of fileIndices) cleanup(() => this.octree.decRefCount(fileIndex))
        if (failure) throw failure
      },
      rollback: () => {
        settled = true
      },
    }
  }

  /** Adapter/device lost 入口：释放本实例 residency，三类 dirty 一次置位等待 restore 后重建。 */
  onDeviceLost(): void {
    if (this.destroyed) return
    this.lifecycleGeneration++
    let failure: unknown
    const cleanup = (operation: () => void): void => {
      try {
        operation()
      } catch (error) {
        failure ??= error
      }
    }
    for (const fileIndex of [...this.loadInterestFiles]) {
      cleanup(() => this.finishLoadInterest(fileIndex))
    }
    for (const fileIndex of [...this.prefetchInterestFiles]) {
      this.prefetchInterestFiles.delete(fileIndex)
      cleanup(() => this.octree.endLoadInterest(fileIndex))
    }
    for (const fileIndex of this.ownedFiles) {
      cleanup(() => this.octree.decRefCount(fileIndex, 0))
    }
    this.ownedFiles.clear()
    this.targetNodeFiles.clear()
    this.targetNodeLods.clear()
    this.activeNodeFiles.clear()
    this.residentFileIndices.clear()
    this.pending.clear()
    this.prefetchPending.clear()
    this.pendingDecrements.clear()
    this.pendingVisibleAdds.clear()
    this.removedCandidates.clear()
    this.activePlacements.clear()
    for (const child of this.filePlacements) cleanup(() => child?.destroy())
    this.filePlacements.fill(null)
    for (const retired of this.retiredPlacements) cleanup(() => retired.placement.destroy())
    this.retiredPlacements.length = 0
    cleanup(() => this.environmentPlacement?.destroy())
    this.environmentPlacement = null
    cleanup(() => this.octree.unloadEnvironmentResource())
    for (const info of this.nodeInfos) info.resetLod()
    this.dirtyModifiedPlacements = true
    this.dirtyPlacementSetChanged = true
    this.needsLodUpdate = true
    this.revisionValue++
    if (failure) throw failure
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.lifecycleGeneration++
    let failure: unknown
    const cleanup = (operation: () => void): void => {
      try {
        operation()
      } catch (error) {
        failure ??= error
      }
    }
    cleanup(() => this.deviceLostLease?.dispose())
    this.deviceLostLease = null
    const hadActive = this.activePlacements.size > 0
    for (const fileIndex of [...this.loadInterestFiles]) {
      cleanup(() => this.finishLoadInterest(fileIndex))
    }
    for (const fileIndex of [...this.prefetchInterestFiles]) {
      this.prefetchInterestFiles.delete(fileIndex)
      cleanup(() => this.octree.endLoadInterest(fileIndex))
    }
    for (const fileIndex of this.ownedFiles) {
      cleanup(() => this.octree.decRefCount(fileIndex, 0))
    }
    this.ownedFiles.clear()
    this.targetNodeFiles.clear()
    this.targetNodeLods.clear()
    this.activeNodeFiles.clear()
    this.residentFileIndices.clear()
    this.pending.clear()
    this.prefetchPending.clear()
    this.pendingDecrements.clear()
    this.pendingVisibleAdds.clear()
    this.removedCandidates.clear()
    this.activePlacements.clear()
    cleanup(() => this.environmentPlacement?.destroy())
    this.environmentPlacement = null
    if (this.ownsEnvironmentRef) {
      cleanup(() => this.octree.decEnvironmentRefCount())
      this.ownsEnvironmentRef = false
    }
    for (const placement of this.filePlacements) cleanup(() => placement?.destroy())
    this.filePlacements.fill(null)
    for (const retired of this.retiredPlacements) cleanup(() => retired.placement.destroy())
    this.retiredPlacements.length = 0
    for (const info of this.nodeInfos) info.resetLod()
    this.revisionValue++
    if (hadActive) cleanup(() => this.invalidation.invalidateWorld('lod-dispose'))
    if (failure) throw failure
  }

  dispose(): void {
    this.destroy()
  }

  private planTargets(targets: ReadonlyMap<number, number>): {
    readonly files: Map<number, number>
    readonly lods: Map<number, number>
  } {
    const files = new Map<number, number>()
    const lods = new Map<number, number>()
    for (const [nodeIndex, lodIndex] of targets) {
      if (
        !Number.isSafeInteger(nodeIndex) ||
        nodeIndex < 0 ||
        nodeIndex >= this.octree.nodes.length
      ) {
        throw new Error(`非法 GSplat Octree nodeIndex: ${nodeIndex}`)
      }
      if (lodIndex === -1) continue
      if (!Number.isSafeInteger(lodIndex) || lodIndex < this.rangeMin || lodIndex > this.rangeMax) {
        throw new Error(`非法 GSplat Octree lodIndex: ${lodIndex}`)
      }
      const lod = this.octree.nodes[nodeIndex].lods[lodIndex]
      if (!lod || lod.fileIndex < 0 || lod.count === 0) continue
      files.set(nodeIndex, lod.fileIndex)
      lods.set(nodeIndex, lodIndex)
    }
    return { files, lods }
  }

  private promoteNode(nodeIndex: number, fileIndex: number, lodIndex: number): boolean {
    const current = this.activeNodeFiles.get(nodeIndex)
    if (current === fileIndex && this.nodeInfos[nodeIndex].currentLod === lodIndex) return false
    if (current !== undefined && current !== fileIndex) this.removedCandidates.add(current)
    this.activeNodeFiles.set(nodeIndex, fileIndex)
    this.nodeInfos[nodeIndex].currentLod = lodIndex
    this.pendingVisibleAdds.delete(nodeIndex)
    this.pendingDecrements.delete(nodeIndex)
    return true
  }

  private refreshPending(): void {
    this.pending.clear()
    for (const fileIndex of new Set(this.targetNodeFiles.values())) {
      if (!this.octree.getFileResource(fileIndex)) this.pending.add(fileIndex)
    }
  }

  private finishLoadInterest(fileIndex: number): void {
    if (!this.loadInterestFiles.delete(fileIndex)) return
    this.octree.endLoadInterest(fileIndex)
  }

  private resolveRange(maxLod: number): { readonly min: number; readonly max: number } {
    const min = Math.max(0, Math.min(this.placement.lodRangeMin ?? 0, maxLod))
    return {
      min,
      max: Math.max(min, Math.min(this.placement.lodRangeMax ?? maxLod, maxLod)),
    }
  }

  private restoreLodEvaluation(
    range: { readonly min: number; readonly max: number },
    nodes: readonly {
      readonly optimalLod: number
      readonly worldDistance: number
      readonly budgetBucket: number
    }[]
  ): void {
    this.rangeMin = range.min
    this.rangeMax = range.max
    for (let index = 0; index < this.nodeInfos.length; index++) {
      const previous = nodes[index]
      this.nodeInfos[index].optimalLod = previous.optimalLod
      this.nodeInfos[index].worldDistance = previous.worldDistance
      this.nodeInfos[index].budgetBucket = previous.budgetBucket
    }
  }

  private validateLodParams(params: GSplatOctreeLodParams): void {
    if (!Number.isFinite(params.lodBehindPenalty) || params.lodBehindPenalty < 1) {
      throw new Error(`非法 GSplat lodBehindPenalty: ${params.lodBehindPenalty}`)
    }
    if (!Number.isSafeInteger(params.lodUnderfillLimit) || params.lodUnderfillLimit < 0) {
      throw new Error(`非法 GSplat lodUnderfillLimit: ${params.lodUnderfillLimit}`)
    }
    if (
      params.lodRefinementMode !== undefined &&
      params.lodRefinementMode !== 'step' &&
      params.lodRefinementMode !== 'direct'
    ) {
      throw new Error(`非法 GSplat lodRefinementMode: ${params.lodRefinementMode}`)
    }
  }

  private ensureLodMinDistanceThresholds(
    maxLod: number,
    baseDistance: number,
    multiplier: number
  ): Float32Array {
    const requiredLength = maxLod + 1
    if (!this.lodMinDistanceThresholds || this.lodMinDistanceThresholds.length < requiredLength) {
      this.lodMinDistanceThresholds = new Float32Array(requiredLength)
    }
    let threshold = baseDistance
    this.lodMinDistanceThresholds[1] = threshold
    for (let lodIndex = 2; lodIndex <= maxLod; lodIndex++) {
      threshold *= multiplier
      this.lodMinDistanceThresholds[lodIndex] = threshold
    }
    return this.lodMinDistanceThresholds
  }

  private evaluateNodeLods(
    camera: THREE.Camera,
    maxLod: number,
    lodBaseDistance: number,
    lodMultiplier: number,
    rangeMin: number,
    rangeMax: number,
    params: GSplatOctreeLodParams,
    uniformScale: number,
    accumulateSplats: boolean,
    globalMaxDistanceForBuckets: number
  ): number {
    if (!Number.isFinite(lodBaseDistance) || lodBaseDistance <= 0) {
      throw new Error(`非法 GSplat lodBaseDistance: ${lodBaseDistance}`)
    }
    if (!Number.isFinite(lodMultiplier) || lodMultiplier <= 1) {
      throw new Error(`非法 GSplat lodMultiplier: ${lodMultiplier}`)
    }
    const fovScale = this.getFovScale(camera)

    camera.getWorldPosition(this.worldCameraPosition)
    camera.getWorldDirection(this.worldCameraForward)
    this.inverseWorld.copy(this.placement.node.matrixWorld).invert()
    this.localCameraPosition.copy(this.worldCameraPosition).applyMatrix4(this.inverseWorld)
    this.localCameraForward
      .copy(this.worldCameraForward)
      .transformDirection(this.inverseWorld)
      .normalize()

    const thresholds =
      maxLod >= 1
        ? this.ensureLodMinDistanceThresholds(maxLod, lodBaseDistance, lodMultiplier)
        : null
    const bucketScale =
      globalMaxDistanceForBuckets > 0 ? NUM_BUCKETS / Math.sqrt(globalMaxDistanceForBuckets) : 0
    const bounds = this.octree.nodeBoundsMinMax
    const px = this.localCameraPosition.x
    const py = this.localCameraPosition.y
    const pz = this.localCameraPosition.z
    const fwx = this.localCameraForward.x
    const fwy = this.localCameraForward.y
    const fwz = this.localCameraForward.z
    let totalSplats = 0

    for (let nodeIndex = 0; nodeIndex < this.octree.nodes.length; nodeIndex++) {
      const offset = nodeIndex * 6
      const qx = THREE.MathUtils.clamp(px, bounds[offset], bounds[offset + 3])
      const qy = THREE.MathUtils.clamp(py, bounds[offset + 1], bounds[offset + 4])
      const qz = THREE.MathUtils.clamp(pz, bounds[offset + 2], bounds[offset + 5])
      const dx = qx - px
      const dy = qy - py
      const dz = qz - pz
      const actualDistance = Math.sqrt(dx * dx + dy * dy + dz * dz)
      let penalizedDistance = actualDistance
      if (params.lodBehindPenalty > 1 && actualDistance > 0.01) {
        const dotOverDistance = (fwx * dx + fwy * dy + fwz * dz) / actualDistance
        if (dotOverDistance < 0) {
          penalizedDistance *= 1 + -dotOverDistance * (params.lodBehindPenalty - 1)
        }
      }
      const fovAdjustedDistance = penalizedDistance * fovScale
      let optimalLodIndex: number
      if (maxLod === 0 || fovAdjustedDistance < lodBaseDistance) {
        optimalLodIndex = 0
      } else {
        optimalLodIndex = maxLod
        while (
          optimalLodIndex > 1 &&
          thresholds &&
          fovAdjustedDistance < thresholds[optimalLodIndex]
        ) {
          optimalLodIndex--
        }
      }
      optimalLodIndex = THREE.MathUtils.clamp(optimalLodIndex, rangeMin, rangeMax)
      const nodeInfo = this.nodeInfos[nodeIndex]
      nodeInfo.optimalLod = optimalLodIndex
      nodeInfo.worldDistance = fovAdjustedDistance * uniformScale
      if (bucketScale > 0) {
        const bucket = Math.sqrt(nodeInfo.worldDistance) * bucketScale
        nodeInfo.budgetBucket = Math.min(NUM_BUCKETS - 1, Math.floor(bucket) >>> 0)
      }
      if (accumulateSplats) {
        totalSplats += this.octree.nodes[nodeIndex].lods[optimalLodIndex]?.count ?? 0
      }
    }
    return totalSplats
  }

  /**
   * 与旧 GSplat LOD 语义保持一致：透视相机按 FOV/aspect 调整距离，正交相机使用
   * 中性的距离比例。正交相机没有 fov/aspect，直接读取会产生 NaN 并把节点错误选到
   * 不存在的最粗 LOD。
   */
  private getFovScale(camera: THREE.Camera): number {
    if (!(camera instanceof THREE.PerspectiveCamera)) return 1

    let tanHalfVerticalFov = Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5))
    if ((camera as THREE.PerspectiveCamera & { horizontalFov?: boolean }).horizontalFov) {
      tanHalfVerticalFov /= camera.aspect
    }
    const tanHalfHorizontalFov = tanHalfVerticalFov * camera.aspect
    return Math.min(tanHalfVerticalFov, tanHalfHorizontalFov) / REF_TAN_HALF_FOV
  }

  private prefetchLod(fileIndex: number): void {
    if (fileIndex === -1) return
    const resource = this.octree.ensureFileResource(fileIndex)
    if (resource) {
      if (this.prefetchInterestFiles.delete(fileIndex)) this.octree.endLoadInterest(fileIndex)
      this.prefetchPending.delete(fileIndex)
      return
    }
    if (!this.prefetchInterestFiles.has(fileIndex)) {
      this.octree.beginLoadInterest(fileIndex)
      this.prefetchInterestFiles.add(fileIndex)
    }
    this.prefetchPending.add(fileIndex)
  }

  private pollPrefetchCompletions(): boolean {
    let completed = false
    for (const fileIndex of [...this.prefetchPending]) {
      this.octree.ensureFileResource(fileIndex)
      if (!this.octree.getFileResource(fileIndex)) continue
      this.prefetchPending.delete(fileIndex)
      if (this.prefetchInterestFiles.delete(fileIndex)) this.octree.endLoadInterest(fileIndex)
      completed = true
    }
    return completed
  }

  private pollEnvironmentCompletion(): boolean {
    if (!this.ownsEnvironmentRef || this.environmentPlacement) return false
    const resource = this.octree.ensureEnvironmentResource()
    if (!resource || typeof this.placement.createChildPlacement !== 'function') return false
    const hadActive = this.activePlacements.size > 0
    const environment = this.placement.createChildPlacement(resource, 0)
    environment.isEnvironment = true
    if (resource.aabb) environment.aabb = resource.aabb
    this.environmentPlacement = environment
    if (this.placement.environmentVisible) this.activePlacements.add(environment)
    resource.releaseTextureSources?.()
    this.dirtyModifiedPlacements = true
    if (!hadActive) this.dirtyPlacementSetChanged = true
    return true
  }

  private syncFilePlacements(): void {
    if (typeof this.placement.createChildPlacement !== 'function') return
    const hadActive = this.activePlacements.size > 0
    const requiredIntervals = new Map<number, Map<number, number>>()
    for (const [nodeIndex, fileIndex] of this.targetNodeFiles) {
      let entries = requiredIntervals.get(fileIndex)
      if (!entries) {
        entries = new Map()
        requiredIntervals.set(fileIndex, entries)
      }
      entries.set(nodeIndex, this.targetNodeLods.get(nodeIndex)!)
    }
    for (const [nodeIndex, fileIndex] of this.activeNodeFiles) {
      let entries = requiredIntervals.get(fileIndex)
      if (!entries) {
        entries = new Map()
        requiredIntervals.set(fileIndex, entries)
      }
      if (!entries.has(nodeIndex)) entries.set(nodeIndex, this.nodeInfos[nodeIndex].currentLod)
    }

    for (const [fileIndex, entries] of requiredIntervals) {
      this.removedCandidates.delete(fileIndex)
      let child = this.filePlacements[fileIndex]
      if (!child) {
        child = this.placement.createChildPlacement(
          null,
          this.octree.files[fileIndex]?.lodLevel ?? entries.values().next().value ?? 0
        )
        this.filePlacements[fileIndex] = child
      }
      child.intervals.clear()
      for (const [nodeIndex, lodIndex] of entries) {
        const lod = this.octree.nodes[nodeIndex].lods[lodIndex]
        child.intervals.set(nodeIndex, {
          start: lod.offset,
          end: lod.offset + lod.count - 1,
        })
      }
      const resource = this.octree.getFileResource(fileIndex)
      if (resource && child.resource !== resource) child.resource = resource
      if (resource && [...this.activeNodeFiles.values()].includes(fileIndex)) {
        this.activePlacements.add(child)
      }
    }

    for (let fileIndex = 0; fileIndex < this.filePlacements.length; fileIndex++) {
      const child = this.filePlacements[fileIndex]
      if (!child || requiredIntervals.has(fileIndex)) continue
      this.activePlacements.delete(child)
      this.filePlacements[fileIndex] = null
      this.removedCandidates.add(fileIndex)
      this.retiredPlacements.push({ fileIndex, placement: child })
    }
    const hasActive = this.activePlacements.size > 0
    if (hadActive !== hasActive) this.dirtyPlacementSetChanged = true
  }

  private collectRequiredFiles(): Set<number> {
    return new Set([...this.targetNodeFiles.values(), ...this.activeNodeFiles.values()])
  }

  private captureSnapshot(): InstanceSnapshot {
    const placementStates = new Map<GSplatPlacement, PlacementSnapshot>()
    for (const child of [
      ...this.filePlacements,
      ...this.retiredPlacements.map((retired) => retired.placement),
      this.environmentPlacement,
    ]) {
      if (!child || placementStates.has(child)) continue
      placementStates.set(child, {
        resource: child.resource,
        intervals: new Map(child.intervals),
      })
    }
    return {
      targetNodeFiles: new Map(this.targetNodeFiles),
      targetNodeLods: new Map(this.targetNodeLods),
      activeNodeFiles: new Map(this.activeNodeFiles),
      residentFileIndices: new Set(this.residentFileIndices),
      pending: new Set(this.pending),
      prefetchPending: new Set(this.prefetchPending),
      pendingDecrements: new Map(this.pendingDecrements),
      pendingVisibleAdds: new Map(this.pendingVisibleAdds),
      removedCandidates: new Set(this.removedCandidates),
      activePlacements: new Set(this.activePlacements),
      environmentPlacement: this.environmentPlacement,
      filePlacements: [...this.filePlacements],
      retiredPlacements: [...this.retiredPlacements],
      placementStates,
      ownedFiles: new Set(this.ownedFiles),
      loadInterestFiles: new Set(this.loadInterestFiles),
      prefetchInterestFiles: new Set(this.prefetchInterestFiles),
      currentLods: this.nodeInfos.map((info) => info.currentLod),
      optimalLods: this.nodeInfos.map((info) => info.optimalLod),
      worldDistances: this.nodeInfos.map((info) => info.worldDistance),
      budgetBuckets: this.nodeInfos.map((info) => info.budgetBucket),
      rangeMin: this.rangeMin,
      rangeMax: this.rangeMax,
      revision: this.revisionValue,
      needsLodUpdate: this.needsLodUpdate,
      dirtyModifiedPlacements: this.dirtyModifiedPlacements,
      dirtyPlacementSetChanged: this.dirtyPlacementSetChanged,
      environmentVisible: this.placement.environmentVisible,
    }
  }

  private restoreSnapshot(snapshot: InstanceSnapshot): void {
    let failure: unknown
    const restore = (operation: () => void): void => {
      try {
        operation()
      } catch (error) {
        failure ??= error
      }
    }
    const snapshotPlacements = new Set(snapshot.placementStates.keys())
    const currentPlacements = new Set<GSplatPlacement>()
    for (const child of this.filePlacements) if (child) currentPlacements.add(child)
    for (const retired of this.retiredPlacements) currentPlacements.add(retired.placement)
    if (this.environmentPlacement) currentPlacements.add(this.environmentPlacement)

    for (const fileIndex of this.ownedFiles) {
      if (!snapshot.ownedFiles.has(fileIndex)) {
        restore(() => this.octree.decRefCount(fileIndex, 0))
      }
    }
    for (const fileIndex of snapshot.ownedFiles) {
      if (!this.ownedFiles.has(fileIndex)) restore(() => this.octree.incRefCount(fileIndex))
    }
    for (const fileIndex of this.loadInterestFiles) {
      if (!snapshot.loadInterestFiles.has(fileIndex)) {
        restore(() => this.octree.endLoadInterest(fileIndex))
      }
    }
    for (const fileIndex of snapshot.loadInterestFiles) {
      if (!this.loadInterestFiles.has(fileIndex)) {
        restore(() => this.octree.beginLoadInterest(fileIndex))
      }
    }
    for (const fileIndex of this.prefetchInterestFiles) {
      if (!snapshot.prefetchInterestFiles.has(fileIndex)) {
        restore(() => this.octree.endLoadInterest(fileIndex))
      }
    }
    for (const fileIndex of snapshot.prefetchInterestFiles) {
      if (!this.prefetchInterestFiles.has(fileIndex)) {
        restore(() => this.octree.beginLoadInterest(fileIndex))
      }
    }
    for (const child of currentPlacements) {
      if (!snapshotPlacements.has(child)) restore(() => child.destroy())
    }
    for (const [child, state] of snapshot.placementStates) {
      child.resource = state.resource
      child.intervals.clear()
      for (const [nodeIndex, interval] of state.intervals) {
        child.intervals.set(nodeIndex, interval)
      }
    }

    replaceMap(this.targetNodeFiles, snapshot.targetNodeFiles)
    replaceMap(this.targetNodeLods, snapshot.targetNodeLods)
    replaceMap(this.activeNodeFiles, snapshot.activeNodeFiles)
    replaceSet(this.residentFileIndices, snapshot.residentFileIndices)
    replaceSet(this.pending, snapshot.pending)
    replaceSet(this.prefetchPending, snapshot.prefetchPending)
    replaceMap(this.pendingDecrements, snapshot.pendingDecrements)
    replaceMap(this.pendingVisibleAdds, snapshot.pendingVisibleAdds)
    replaceSet(this.removedCandidates, snapshot.removedCandidates)
    replaceSet(this.activePlacements, snapshot.activePlacements)
    replaceSet(this.ownedFiles, snapshot.ownedFiles)
    replaceSet(this.loadInterestFiles, snapshot.loadInterestFiles)
    replaceSet(this.prefetchInterestFiles, snapshot.prefetchInterestFiles)
    for (let index = 0; index < this.filePlacements.length; index++) {
      this.filePlacements[index] = snapshot.filePlacements[index] ?? null
    }
    this.retiredPlacements.length = 0
    this.retiredPlacements.push(...snapshot.retiredPlacements)
    this.environmentPlacement = snapshot.environmentPlacement
    this.placement.environmentVisible = snapshot.environmentVisible
    for (let index = 0; index < this.nodeInfos.length; index++) {
      this.nodeInfos[index].currentLod = snapshot.currentLods[index]
      this.nodeInfos[index].optimalLod = snapshot.optimalLods[index]
      this.nodeInfos[index].worldDistance = snapshot.worldDistances[index]
      this.nodeInfos[index].budgetBucket = snapshot.budgetBuckets[index]
    }
    this.rangeMin = snapshot.rangeMin
    this.rangeMax = snapshot.rangeMax
    this.revisionValue = snapshot.revision
    this.needsLodUpdate = snapshot.needsLodUpdate
    this.dirtyModifiedPlacements = snapshot.dirtyModifiedPlacements
    this.dirtyPlacementSetChanged = snapshot.dirtyPlacementSetChanged
    if (failure) throw failure
  }

  private createLodTransitionStage(
    snapshot: InstanceSnapshot,
    generation: number
  ): GSplatOctreeTransitionStage {
    const changed =
      !mapsEqual(snapshot.targetNodeFiles, this.targetNodeFiles) ||
      !mapsEqual(snapshot.targetNodeLods, this.targetNodeLods) ||
      snapshot.currentLods.some((lod, index) => lod !== this.nodeInfos[index].currentLod)
    let committed = false
    let settled = false
    return {
      changed,
      activePlacements: Object.freeze([...this.activePlacements]),
      commit: () => {
        if (settled || committed) return
        this.assertStageGeneration(generation)
        committed = true
      },
      rollback: () => {
        if (settled) return
        if (generation !== this.lifecycleGeneration) {
          settled = true
          return
        }
        try {
          this.restoreSnapshot(snapshot)
        } finally {
          settled = true
        }
      },
      finalize: () => {
        if (settled) return
        if (generation !== this.lifecycleGeneration) {
          settled = true
          return
        }
        if (!committed) {
          this.restoreSnapshot(snapshot)
          settled = true
          return
        }
        settled = true
        if (changed) this.invalidation.invalidateWorld('lod-target-changed')
      },
    }
  }

  private assertActive(): void {
    if (this.destroyed) throw new Error('GSplatOctreeInstance 已销毁')
    if (this.octree.isDestroyed) throw new Error('GSplatOctree 已销毁')
  }

  private assertStageGeneration(generation: number): void {
    if (generation !== this.lifecycleGeneration) {
      throw new Error('GSplatOctreeInstance stage generation 已失效')
    }
  }
}

function mapsEqual(left: ReadonlyMap<number, number>, right: ReadonlyMap<number, number>): boolean {
  if (left.size !== right.size) return false
  for (const [key, value] of left) {
    if (right.get(key) !== value) return false
  }
  return true
}

function setsEqual<T>(left: ReadonlySet<T>, right: ReadonlySet<T>): boolean {
  if (left.size !== right.size) return false
  for (const value of left) if (!right.has(value)) return false
  return true
}

function replaceMap<K, V>(target: Map<K, V>, source: ReadonlyMap<K, V>): void {
  target.clear()
  for (const [key, value] of source) target.set(key, value)
}

function replaceSet<T>(target: Set<T>, source: ReadonlySet<T>): void {
  target.clear()
  for (const value of source) target.add(value)
}
